import { Lookups } from '../../lists/lookups.enum';
import { LookupBase } from '../utils/lookup-base.model';
import { generateShortUuid } from '../../../../../../../ag-common-svc/projects/ag-common-svc/src/shared/utils/generate-item-uuid';
import { ButtonType } from './buttons-type.model';

export enum FieldValidationType {
  Email = 'email',
  Phone = 'phone',
  Number = 'number',
  Url = 'url',
}

export const availableValidationTypes = [
  { value: FieldValidationType.Email, description: 'Email' },
  { value: FieldValidationType.Phone, description: 'Phone' },
  { value: FieldValidationType.Number, description: 'Number' },
  { value: FieldValidationType.Url, description: 'URL' },
];

export enum FormFieldType {
  text = 'dxTextBox',
  textArea = 'dxTextArea',
  select = 'dxSelectBox',
  radio = 'dxRadioGroup',
  checkbox = 'dxCheckBox',
  date = 'dxDateBox',
  number = 'dxNumberBox',
  empty = 'empty',
}

export const availableFieldTypes = [
  { type: FormFieldType.text, label: 'Text Input' },
  { type: FormFieldType.textArea, label: 'Text Area' },
  { type: FormFieldType.select, label: 'Select' },
  { type: FormFieldType.number, label: 'Number Box' },
  { type: FormFieldType.radio, label: 'Radio Group' },
  { type: FormFieldType.checkbox, label: 'Checkbox' },
  { type: FormFieldType.date, label: 'Date' },
  { type: FormFieldType.empty, label: 'Empty' },
];

export enum FieldMaskType {
  none = 'none',
  phone = 'phone',
  creditCard = 'creditCard',
  ssn = 'ssn',
  postalCode = 'postalCode',
}

export enum TextFieldValidationTypes {
  email = 'email',
  password = 'password',
  tel = 'tel',
  url = 'url',
}

export const availableMasks = [
  { type: FieldMaskType.none, label: 'None' },
  { type: FieldMaskType.phone, label: 'Phone Number' },
  { type: FieldMaskType.creditCard, label: 'Credit Card' },
  { type: FieldMaskType.ssn, label: 'SSN' },
  { type: FieldMaskType.postalCode, label: 'Postal Code' },
];

export enum MaskConfigKeys {
  mask = 'mask',
  maskRules = 'maskRules',
  showMaskMode = 'showMaskMode',
}

export class MaskConfig {
  [MaskConfigKeys.mask]: string;
  [MaskConfigKeys.maskRules]?: Record<string, RegExp | ((char: string) => boolean)>;
  [MaskConfigKeys.showMaskMode]?: 'always' | 'onFocus';
}

export enum NumberConfigKeys {
  max = 'max',
  min = 'min',
  step = 'step',
  precision = 'precision',
  format = 'format',
}

export enum LayoutType {
  vertical = 'vertical',
  horizontal = 'horizontal',
}

export const availableLayoutTypes: LookupBase[] = [
  { value: LayoutType.vertical, description: 'Vertical' },
  { value: LayoutType.horizontal, description: 'Horizontal' },
];

export class NumberConfig {
  [NumberConfigKeys.max]?: number;
  [NumberConfigKeys.min]?: number;
  [NumberConfigKeys.step]?: number;
  [NumberConfigKeys.precision]?: number;
  [NumberConfigKeys.format]?: NumberValueFormat;
}

export enum DateType {
  date = 'date',
  datetime = 'datetime',
  time = 'time',
}

export const availableDateTypes: LookupBase[] = [
  { value: DateType.date, description: 'Date' },
  { value: DateType.datetime, description: 'Date & Time' },
  { value: DateType.time, description: 'Time' },
];

export enum BoxAlignment {
  top = 'top',
  center = 'center',
  bottom = 'bottom',
}

export const availableBoxAlignment: LookupBase[] = [
  { value: BoxAlignment.top, description: 'Top' },
  { value: BoxAlignment.center, description: 'Center' },
  { value: BoxAlignment.bottom, description: 'Bottom' },
];

export enum NumberValueFormat {
  Integer = '#',
  Currency = '$ #,##0.##',
  Accounting = '$ #,##0.##;($ #,##0.##)',
  Percent = '#0%',
  FixedPoint = '#,##0.00',
  Weight = '#0.## lb',
}

export const availableNumberFormats: LookupBase[] = [
  { value: NumberValueFormat.Integer, description: 'Integer' },
  { value: NumberValueFormat.Currency, description: 'Currency' },
  { value: NumberValueFormat.Accounting, description: 'Accounting' },
  { value: NumberValueFormat.Percent, description: 'Percent' },
  { value: NumberValueFormat.FixedPoint, description: 'Fixed Point' },
  { value: NumberValueFormat.Weight, description: 'Weight' },
];

export const fieldMaskLookupMap: Record<FieldMaskType, string> = {
  [FieldMaskType.none]: '',
  [FieldMaskType.phone]: '(000) 000-0000',
  [FieldMaskType.creditCard]: '0000-0000-0000-0000',
  [FieldMaskType.ssn]: '000-00-0000',
  [FieldMaskType.postalCode]: '00000-0000',
};

export const fieldMaskConfig: Record<FieldMaskType, MaskConfig> = {
  [FieldMaskType.none]: null,
  [FieldMaskType.phone]: {
    mask: '+1 (X00) 000-0000',
    maskRules: {
      X: /[02-9]/,
    },
    showMaskMode: 'always',
  },
  [FieldMaskType.creditCard]: {
    // TODO: there a lot of different mask formats for creditCard
    mask: '0000-0000-0000-0000',
    showMaskMode: 'onFocus',
  },
  [FieldMaskType.ssn]: {
    mask: '000-00-0000',
    showMaskMode: 'always',
  },
  [FieldMaskType.postalCode]: {
    mask: '00000-0000',
    showMaskMode: 'onFocus',
  },
};

export enum FormFieldConfigKeys {
  id = 'id',
  type = 'type',
  label = 'label',
  name = 'name',
  placeholder = 'placeholder',
  description = 'description',
  required = 'required',
  readOnly = 'readOnly',
  validationRules = 'validationRules',
  resize = 'resize',
  height = 'height',
  colSpan = 'colSpan',
  order = 'order',
  maskType = 'maskType',
  maskConfig = 'maskConfig',
  useCustomOptions = 'useCustomOptions',
  lookupValue = 'lookupValue',
  customOptions = 'customOptions',
  numberConfig = 'numberConfig',
  layout = 'layout',
  dateType = 'dateType',
  boxAlignment = 'boxAlignment',
  iconSize = 'iconSize',
  useButtonStyle = 'useButtonStyle',
  buttonType = 'buttonType',
}

export class FormFieldConfig {
  [FormFieldConfigKeys.id]: string;
  [FormFieldConfigKeys.type]: FormFieldType;
  [FormFieldConfigKeys.label]: string;
  [FormFieldConfigKeys.name]: string;
  [FormFieldConfigKeys.placeholder]?: string;
  [FormFieldConfigKeys.description]?: string;
  [FormFieldConfigKeys.required]: boolean;
  [FormFieldConfigKeys.readOnly]?: boolean;
  [FormFieldConfigKeys.validationRules]?: TextFieldValidationTypes[];
  [FormFieldConfigKeys.resize]?: 'none' | 'both' | 'horizontal' | 'vertical';
  [FormFieldConfigKeys.height]?: number;
  [FormFieldConfigKeys.colSpan]: number;
  [FormFieldConfigKeys.order]: number;

  [FormFieldConfigKeys.maskType]: FieldMaskType;
  [FormFieldConfigKeys.maskConfig]?: MaskConfig;

  [FormFieldConfigKeys.useCustomOptions]: boolean;
  [FormFieldConfigKeys.lookupValue]?: Lookups;
  [FormFieldConfigKeys.customOptions]?: { text: string }[];

  [FormFieldConfigKeys.numberConfig]?: NumberConfig;

  [FormFieldConfigKeys.layout]?: LayoutType;

  [FormFieldConfigKeys.dateType]?: DateType;

  [FormFieldConfigKeys.boxAlignment]?: BoxAlignment;
  [FormFieldConfigKeys.iconSize]?: number;
  [FormFieldConfigKeys.useButtonStyle]?: boolean;
  [FormFieldConfigKeys.buttonType]?: ButtonType;

  constructor(config: {
    type: FormFieldType;
    label?: string;
    name?: string;
    placeholder?: string;
    description?: string;
    required?: boolean;
    readOnly?: boolean;
    validationRules?: TextFieldValidationTypes[];
    options?: LookupBase[];
    resize?: 'none' | 'both' | 'horizontal' | 'vertical';
    colSpan?: number;
    order?: number;
    maskType?: FieldMaskType;
    useCustomOptions?: boolean;
  }) {
    this.id = generateShortUuid();
    this.type = config?.type ?? FormFieldType.text;
    this.label = config?.label ?? '';
    this.name = config?.name ?? '';
    this.placeholder = config?.placeholder ?? '';
    this.description = config?.description ?? '';
    this.required = config?.required ?? false;
    this.readOnly = config?.readOnly ?? false;
    this.validationRules = config?.validationRules ?? [];
    this.useCustomOptions = config?.useCustomOptions || false;
    this.resize = config?.resize ?? null;
    this.colSpan = config?.colSpan ?? 1;
    this.order = config?.order ?? 0;
    this.maskType = config?.maskType ?? FieldMaskType.none;

    // to fix hasChanges that no has maskConfig in initial value but added without user interaction
    if (this.maskType === FieldMaskType.none || !this.maskType) {
      this.maskConfig = null;
    }

    switch (this.type) {
      case FormFieldType.number:
        this.numberConfig = new NumberConfig();
        this.numberConfig.format = NumberValueFormat.Integer;
        this.numberConfig.step = 1;
        break;

      case FormFieldType.radio:
        this.layout = LayoutType.horizontal;
        this.boxAlignment = BoxAlignment.center;
        this.useButtonStyle = false;
        this.buttonType = ButtonType.default;
        this.useCustomOptions = true;
        this.customOptions = [{ text: 'Yes' }, { text: 'No' }];
        break;

      case FormFieldType.date:
        this.dateType = DateType.date;
        break;

      case FormFieldType.checkbox:
        this.layout = LayoutType.horizontal;
        this.boxAlignment = BoxAlignment.center;
        this.useButtonStyle = false;
        this.buttonType = ButtonType.default;
        this.iconSize = 22; // default width and height from devextreme styles
        break;
    }
  }

  setMaskType(value: FieldMaskType | undefined) {
    this.maskType = value;

    if (!value || value === FieldMaskType.none) {
      this.maskConfig = null;
      return;
    }

    const maskConfig = fieldMaskConfig[value];
    if (maskConfig) {
      this.maskConfig = {
        mask: fieldMaskLookupMap[value],
        maskRules: maskConfig.maskRules,
        showMaskMode: maskConfig.showMaskMode,
      };
    }
  }

  toJSON() {
    const out: any = {
      id: this.id,
      type: this.type,
      label: this.label,
      name: this.name,
      required: !!this.required,
      readOnly: !!this.readOnly,
      validationRules: this.validationRules ?? [],
      colSpan: this.colSpan,
      order: this.order,
      maskType: this.maskType,
      useCustomOptions: !!this.useCustomOptions,
      placeholder: this.placeholder,
      description: this.description,
      resize: this.resize,
      height: this.height,
      maskConfig: this.maskConfig,
      lookupValue: this.lookupValue,
      customOptions: this.customOptions,
      numberConfig: this.numberConfig,
      layout: this.layout,
      dateType: this.dateType,
      boxAlignment: this.boxAlignment,
      iconSize: this.iconSize,
      useButtonStyle: this.useButtonStyle,
      buttonType: this.buttonType,
    };

    Object.keys(out).forEach(k => {
      const v = out[k];
      if (v === undefined || (k === 'maskConfig' && v === null)) {
        delete out[k];
      }
    });

    return out;
  }
}
